import { Button, Modal, confirm } from "@devhop/ui";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreateContractType,
  useDeleteContractType,
  useUpdateContractType,
} from "../api/contract-type-queries";
import { ContractTypeForm } from "./ContractTypeForm";
import type { ContractTypeDTO } from "../../domain/contract-types";

export function ContractTypeManagementModal({
  open,
  onOpenChange,
  types,
  onRefresh,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  types: ContractTypeDTO[];
  onRefresh: () => void;
}) {
  const createModal = useDisclosure();
  const editModal = useDisclosure<ContractTypeDTO>();
  const createContractType = useCreateContractType();
  const updateContractType = useUpdateContractType(editModal.data?.id ?? "");
  const deleteContractType = useDeleteContractType();

  const handleCreate = async (vals: { name: string; description?: string | null; orderIndex?: number }) => {
    try {
      await createContractType.mutateAsync({
        name: vals.name,
        description: vals.description ?? null,
        orderIndex: vals.orderIndex ?? 0,
      });
      createModal.close();
      onRefresh();
    } catch (error) {
      // Error is already handled by onError in the mutation hook
      console.error("Failed to create contract type:", error);
    }
  };

  const handleUpdate = async (vals: { name: string; description?: string | null; orderIndex?: number; isActive?: string }) => {
    if (!editModal.data) return;
    try {
      // Use provided isActive or convert current boolean to string
      const isActive = vals.isActive !== undefined 
        ? vals.isActive 
        : (editModal.data.isActive ? "true" : "false");
      
      await updateContractType.mutateAsync({
        name: vals.name,
        description: vals.description ?? null,
        orderIndex: vals.orderIndex ?? 0,
        isActive,
      });
      editModal.close();
      onRefresh();
    } catch (error) {
      // Error is already handled by onError in the mutation hook
      console.error("Failed to update contract type:", error);
    }
  };

  const handleDelete = async (id: string, typeName: string) => {
    const ok = await confirm({
      title: "ລຶບປະເພດສັນຍາ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບປະເພດສັນຍາ "${typeName}"?`,
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });
    if (ok) {
      try {
        await deleteContractType.mutateAsync(id);
        onRefresh();
      } catch (error) {
        // Error is already handled by onError in the mutation hook
        console.error("Failed to delete contract type:", error);
      }
    }
  };

  return (
    <>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="ຈັດການປະເພດສັນຍາ"
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={createModal.open}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              ເພີ່ມປະເພດສັນຍາ
            </Button>
          </div>

          <div className="rounded-lg border">
            <div className="max-h-[calc(100vh-16rem)] divide-y overflow-y-auto">
              {types.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">
                  ຍັງບໍ່ມີປະເພດສັນຍາ
                </div>
              ) : (
                types.map((type) => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{type.name}</div>
                      {type.description && (
                        <div className="line-clamp-2 text-muted-foreground text-sm">
                          {type.description}
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex flex-shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => editModal.openWith(type)}
                      >
                        ແກ້ໄຂ
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(type.id, type.name)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal
        open={!!createModal.isOpen}
        onOpenChange={createModal.toggle}
        title="ເພີ່ມປະເພດສັນຍາ"
        size="sm"
      >
        <ContractTypeForm
          onSubmit={handleCreate}
          submitting={createContractType.isPending}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editModal.isOpen}
        onOpenChange={editModal.toggle}
        title="ແກ້ໄຂປະເພດສັນຍາ"
        size="sm"
      >
        <ContractTypeForm
          initialValues={editModal.data ?? undefined}
          onSubmit={handleUpdate}
          submitting={updateContractType.isPending}
        />
      </Modal>
    </>
  );
}
